import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { auditApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { LoadingState, Panel, SkeletonRows } from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Who did what to this sample, and when.
 *
 * READ FROM THE AUDIT TRAIL, which is the record of responsibility itself.
 * Receiving, deciding, recording pieces, moving, printing a label and disposing
 * all write an audit entry naming the user and the moment, and that history can
 * never be edited to hide a change (CLAUDE.md section 5.5). Building a second
 * activity store beside it would produce a record free to disagree with the one
 * that matters.
 *
 * READING IT NEEDS `audit.view`, AND WHERE SOMEBODY LACKS IT THIS SAYS SO
 * rather than rendering an empty list. An empty list would assert that nothing
 * has happened to this sample, which is a different statement from "you may not
 * read who acted on it" — and the wrong one.
 */
export function SampleHistory({ sampleId }: { readonly sampleId: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();

  const mayRead = can(PERMISSIONS.AUDIT_VIEW);

  const entries = useQuery({
    queryKey: ['audit', 'sample', sampleId],
    queryFn: () =>
      auditApi.list({ entityType: 'sample', entityId: sampleId, pageSize: 50 }),
    enabled: mayRead,
  });

  if (!mayRead) {
    return (
      <Panel title={t('samples.historyTitle')}>
        <p className="text-body text-default-600">
          {t('samples.historyRestricted')}
        </p>
      </Panel>
    );
  }

  return (
    <Panel
      title={t('samples.historyTitle')}
      description={t('samples.historyHint')}
    >
      {entries.isLoading ? (
        <>
          <LoadingState label={t('common.loading')} />
          <SkeletonRows rows={3} />
        </>
      ) : entries.error ? (
        <p className="text-body text-warning-600">
          {t('samples.historyUnavailable')}
        </p>
      ) : (entries.data?.data ?? []).length === 0 ? (
        <p className="text-body text-default-600">{t('samples.historyNone')}</p>
      ) : (
        <ol className="flex flex-col gap-stack">
          {(entries.data?.data ?? []).map((entry) => (
            <li
              key={entry.id}
              className="min-w-0 border-s-2 border-default-200 ps-3"
            >
              <p className="text-body text-default-900">
                {t(`audit.actions.${entry.action}`, {
                  defaultValue: entry.action,
                })}
              </p>
              <p className="text-caption text-default-600">
                {entry.actor?.displayName ?? t('audit.system')}
              </p>
              <p className="text-caption text-default-600">
                {formatDateTime(entry.occurredAt, { locale })}
              </p>
              {entry.reason ? (
                <p className="text-caption text-default-700">{entry.reason}</p>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      <p className="mt-stack text-caption text-default-600">
        {t('audit.immutableNotice')}
      </p>
    </Panel>
  );
}
